"use client";

import { useState } from "react";
import Link from "next/link";
import { Search, X, ArrowUpRight } from "lucide-react";
import type { FaqItem } from "@/components/ui/FaqAccordion";

type Tone = "base" | "soft" | "elevated";

export type FaqGroup = {
  id: string;
  label: string;
  heading: string;
  tone: Tone;
  faqs: FaqItem[];
};

const toneClass: Record<Tone, string> = {
  base: "bg-bg",
  soft: "bg-bg-soft",
  elevated: "bg-bg-elevated",
};

export function FaqsSearchable({ groups }: { groups: FaqGroup[] }) {
  const [q, setQ] = useState("");
  const [activeId, setActiveId] = useState<string>(groups[0]?.id ?? "");
  const norm = q.trim().toLowerCase();
  const isSearching = norm.length > 0;

  const searchHits = isSearching
    ? groups
        .map((g) => ({
          ...g,
          faqs: g.faqs.filter(
            (f) =>
              f.q.toLowerCase().includes(norm) ||
              f.a.toLowerCase().includes(norm),
          ),
        }))
        .filter((g) => g.faqs.length > 0)
    : [];

  const totalShown = searchHits.reduce((n, g) => n + g.faqs.length, 0);
  const totalAll = groups.reduce((n, g) => n + g.faqs.length, 0);
  const activeGroup = groups.find((g) => g.id === activeId) ?? groups[0];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: groups.flatMap((g) =>
      g.faqs.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    ),
  };

  return (
    <>
      {/* Search bar — own thin stripe */}
      <section className="bg-bg-soft border-b border-border">
        <div className="site-container py-8 lg:py-10">
          <div className="relative max-w-2xl">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 size-5 text-text-muted"
              aria-hidden="true"
            />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search frequently asked questions…"
              className="w-full rounded-pill border border-border-strong bg-bg pl-12 pr-12 py-3.5 text-body text-text placeholder:text-text-muted transition-shadow focus:border-accent focus:outline-none focus:shadow-[0_0_0_3px_rgba(192,247,72,0.18)]"
              aria-label="Search FAQs"
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ("")}
                aria-label="Clear search"
                className="absolute right-4 top-1/2 -translate-y-1/2 inline-flex size-7 items-center justify-center rounded-full bg-bg-soft border border-border-strong text-text-muted transition hover:border-accent hover:text-accent"
              >
                <X className="size-3.5" />
              </button>
            )}
          </div>
          {isSearching && (
            <p className="mt-3 text-small text-text-muted">
              Showing{" "}
              <span className="text-accent font-semibold">{totalShown}</span>{" "}
              of {totalAll} questions matching{" "}
              <span className="text-text">"{q}"</span>.
            </p>
          )}
        </div>
      </section>

      {/* ─── SEARCHING ───────────────────────────────────────────── */}
      {isSearching && searchHits.length === 0 && (
        <section className="bg-bg border-b border-border">
          <div className="site-container py-20 lg:py-24 text-center">
            <p className="text-h3 text-text-muted">
              No questions match "<span className="text-text">{q}</span>" yet.
            </p>
            <p className="mt-3 text-body text-text-muted max-w-md mx-auto">
              Drop us a line — we'll answer directly and add it to the page
              for the next founder who asks.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => setQ("")}
                className="inline-flex items-center gap-2 rounded-pill border border-border-strong px-5 py-2.5 text-small font-semibold text-text transition hover:border-accent hover:text-accent"
              >
                Clear search
              </button>
              <Link
                href="/contact"
                className="inline-flex items-center gap-2 rounded-pill bg-accent px-5 py-2.5 text-small font-bold text-accent-ink transition hover:bg-accent-hover"
              >
                Ask us directly
                <ArrowUpRight className="size-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {isSearching &&
        searchHits.map((g) => (
          <FaqGroupBlock key={g.id} group={g} tone={g.tone} forceOpen />
        ))}

      {/* ─── IDLE: sidebar TOC (lg+) / tab strip (below lg) ─────── */}
      {!isSearching && (
        <section className={`${toneClass[activeGroup.tone]} border-b border-border`}>
          <div className="site-container py-12 lg:py-20">
            {/* Mobile / tablet tab strip */}
            <div
              role="tablist"
              aria-label="FAQ categories"
              className="lg:hidden flex flex-wrap gap-2 mb-8 pb-2 -mx-1 px-1 overflow-x-auto"
            >
              {groups.map((g) => {
                const isActive = activeId === g.id;
                return (
                  <button
                    key={g.id}
                    role="tab"
                    aria-selected={isActive}
                    type="button"
                    onClick={() => setActiveId(g.id)}
                    className={[
                      "inline-flex shrink-0 items-center gap-2 rounded-pill border px-4 py-1.5 text-small transition-all duration-150",
                      isActive
                        ? "border-accent bg-accent text-accent-ink font-semibold"
                        : "border-border-strong text-text-muted hover:border-accent hover:text-accent",
                    ].join(" ")}
                  >
                    {g.label}
                    <span
                      className={[
                        "inline-flex size-5 items-center justify-center rounded-full text-[0.625rem] font-bold",
                        isActive
                          ? "bg-accent-ink/20 text-accent-ink"
                          : "bg-bg-soft text-text-muted",
                      ].join(" ")}
                    >
                      {g.faqs.length}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* lg+ layout: sidebar TOC + content */}
            <div className="lg:grid lg:grid-cols-12 lg:gap-12">
              {/* Sidebar TOC */}
              <aside
                className="hidden lg:block lg:col-span-3"
                aria-label="FAQ categories"
              >
                <div className="sticky top-28">
                  <p className="text-small font-semibold uppercase tracking-[0.2em] text-text-muted">
                    Categories
                  </p>
                  <ul role="tablist" className="mt-5 space-y-1">
                    {groups.map((g) => {
                      const isActive = activeId === g.id;
                      return (
                        <li key={g.id}>
                          <button
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            onClick={() => setActiveId(g.id)}
                            className={[
                              "relative w-full text-left flex items-center justify-between gap-3 rounded-md py-2.5 pl-4 pr-3 text-small transition-colors",
                              isActive
                                ? "text-accent font-semibold bg-bg/40"
                                : "text-text-muted hover:text-accent",
                            ].join(" ")}
                          >
                            {/* Vertical accent bar on the active item's left edge */}
                            <span
                              aria-hidden="true"
                              className={[
                                "absolute left-0 top-1/2 -translate-y-1/2 w-[3px] rounded-full transition-all duration-200",
                                isActive ? "h-6 bg-accent" : "h-0 bg-accent",
                              ].join(" ")}
                            />
                            <span>{g.label}</span>
                            <span
                              className={[
                                "inline-flex size-6 items-center justify-center rounded-full text-[0.6875rem] font-bold transition-colors",
                                isActive
                                  ? "bg-accent/15 text-accent"
                                  : "bg-bg-soft text-text-muted",
                              ].join(" ")}
                            >
                              {g.faqs.length}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </aside>

              {/* Active category content */}
              <div className="lg:col-span-9">
                <p className="text-small font-semibold uppercase tracking-[0.2em] text-accent">
                  {activeGroup.label}
                </p>
                <h2 className="text-h3 mt-3 max-w-3xl">{activeGroup.heading}</h2>
                <div className="mt-8 divide-y divide-border border-y border-border">
                  {activeGroup.faqs.map(({ q, a }) => (
                    <details key={q} className="group py-4">
                      <summary className="flex cursor-pointer items-start justify-between gap-6 text-lead font-semibold list-none transition-colors group-hover:text-accent [&::-webkit-details-marker]:hidden">
                        <span>{q}</span>
                        <span
                          aria-hidden="true"
                          className="mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-full border border-border-strong text-small text-text-muted transition-transform group-open:rotate-45 group-hover:border-accent group-hover:text-accent"
                        >
                          +
                        </span>
                      </summary>
                      <p className="mt-2.5 text-body text-text-muted max-w-prose">
                        {a}
                      </p>
                    </details>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
    </>
  );
}

/* One full FAQ section block — used only for search-results stacking */
function FaqGroupBlock({
  group,
  tone,
  forceOpen = false,
}: {
  group: FaqGroup;
  tone: Tone;
  forceOpen?: boolean;
}) {
  return (
    <section className={`${toneClass[tone]} border-b border-border`}>
      <div className="site-container py-14 lg:py-20">
        <p className="text-small font-semibold uppercase tracking-[0.2em] text-accent">
          {group.label}
        </p>
        <h2 className="text-h3 mt-3 max-w-3xl">{group.heading}</h2>
        <div className="mt-8 max-w-3xl divide-y divide-border border-y border-border">
          {group.faqs.map(({ q, a }) => (
            <details key={q} className="group py-4" open={forceOpen}>
              <summary className="flex cursor-pointer items-start justify-between gap-6 text-lead font-semibold list-none transition-colors group-hover:text-accent [&::-webkit-details-marker]:hidden">
                <span>{q}</span>
                <span
                  aria-hidden="true"
                  className="mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-full border border-border-strong text-small text-text-muted transition-transform group-open:rotate-45 group-hover:border-accent group-hover:text-accent"
                >
                  +
                </span>
              </summary>
              <p className="mt-2.5 text-body text-text-muted max-w-prose">{a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}
